/**
 * CheckUp Service
 *
 * CheckUp Hero assessment API methods.
 * Consolidated under /api/heroes/checkup/:talentId/*
 */

import type {
  CheckUpPillarBand,
  CheckUpSkillBand,
  LanguageTestFailureReason,
  LanguageTestLanguage,
} from '@tonnie/heroes';
import type {
  CheckupInsights,
  IQultureBadgeId,
  IQultureBadgeLowerBounds,
  IQultureCompleteness,
  PitchAnalysisFailureReason,
  PitchHeuristicReason,
} from '@tonnie/types';

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export type CheckUpAssessmentStatus =
  'not_started' | 'in_progress' | 'ready_to_calculate' | 'calculating' | 'completed' | 'failed';

export interface CheckUpAssessment {
  id: string | null;
  talentId: string;
  journeyInstanceId: string | null;
  status: CheckUpAssessmentStatus;
  progress: {
    assistantChatCompleted: boolean;
    motivationalAnswersCompleted: boolean;
    pitchVideoUploaded: boolean;
    videosWatched?: boolean;
    languageTestsCompleted?: boolean;
    /** Current CoPilot Guide step for resume support */
    guideStep?: number;
  };
  canCalculate: boolean;
  /**
   * Every ASSESSMENT step is done and the profile is the only thing between the
   * citizen and their score (F33 · Citizen first run). Server-decided; absent
   * from older servers, which reads as `false`.
   */
  awaitingProfile?: boolean;
  missingSteps: string[];
  chatSessionId?: string;
  calculationJobId?: string;
  startedAt: string | null;
  createdAt: string | null;
  /** Most recent assessment activity */
  lastActivityAt?: string | null;
  /**
   * F31 Phase 3 — present when this route's CheckUp was seeded from the
   * citizen's completed CheckUp for another destination. The SERVER names that destination; the client
   * renders it and never derives it. Absent from older servers, null when the
   * citizen started the assessment themselves.
   */
  seededFrom?: CheckUpSeededFrom | null;
}

/** Where a seeded CheckUp's evidence came from (F31 Phase 3). */
export interface CheckUpSeededFrom {
  /** ISO 3166-1 alpha-2 code of the source journey's destination. */
  destinationCountry: string;
}

export interface CheckUpStartResult {
  id: string;
  status: string;
  nextStep: string;
  chatSessionId: string;
}

export interface MotivationalSubmitResult {
  status: string;
  questionsAnswered: number;
  nextStep: string;
}

export interface MotivationalDraftResult {
  status: string;
  questionsAnswered: number;
}

export interface MotivationalDraftLoadResult {
  hasDraft: boolean;
  answers: Array<{ questionId: string; answer: string }>;
}

export interface WidgetCompletion {
  completed: boolean;
  completedAt?: string;
  data?: Record<string, unknown>;
}

export interface WidgetProgressInput {
  widgetCompletions: Record<string, WidgetCompletion>;
}

export interface WidgetProgressResult {
  status: string;
  widgetCompletions?: Record<string, WidgetCompletion>;
}

export interface PitchVideoResult {
  status: string;
  pitchVideoFileId: string;
  canCalculate: boolean;
  nextStep: string | null;
}

/**
 * The CEFR analysis of one language certificate: the level(s), or the typed
 * reason there are none. A failure is never a completed test — the client
 * must render it as one (H15).
 */
export type LanguageTestAnalysisResult =
  | {
      success: true;
      cefrResult: {
        overall?: string;
        speaking?: string;
        reading?: string;
        writing?: string;
        listening?: string;
      };
      confidence: number;
      /** ISO 639-1 code of the language the certificate attests; null when it does not say. */
      detectedLanguage: string | null;
    }
  | {
      success: false;
      reason: LanguageTestFailureReason;
      detectedLanguage: string | null;
    };

/** Request body for a certificate analysis. The language is required — never assumed. */
export interface AnalyzeLanguageTestInput {
  fileId: string;
  testLanguage: LanguageTestLanguage;
  testType?: string;
}

export interface CalculationTriggerResult {
  jobId: string;
  status: string;
  estimatedTime: number;
  pollingUrl: string;
}

export interface CalculationStatus {
  jobId?: string;
  /**
   * `pending` | `queued` | `processing` | `completed` | `failed` | `cancelled`,
   * and `not_started` when the talent-scoped read found no job at all.
   */
  status: string;
  progressPct?: number;
  progressStep?: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  message?: string;

  /**
   * Stuck-detection, owned by the SERVER (F43 / H16).
   *
   * The client used to decide on its own that 180 s meant stuck, while the
   * server only superseded a job after 15 minutes — so "Try again" appeared
   * twelve minutes before it could do anything. Gate the retry affordance on
   * `isStale`, never on a locally-timed threshold.
   *
   * Optional so an older backend that does not send them degrades to "not
   * stale" rather than to a crash.
   */
  /** Last progress tick — NOT the enqueue time. */
  lastHeartbeatAt?: string;
  /** The server's threshold, in ms, for reference and display. */
  staleAfterMs?: number;
  /** When this specific job becomes retryable. */
  retryableAt?: string;
  /** Whether it already is. This is the one to gate the button on. */
  isStale?: boolean;
}

/**
 * Status of ONE named calculation job. (W5.7 · F26)
 *
 * The id is the one `triggerCalculation` returned AND the one the server
 * persisted on the assessment row, so this is the only status read a client
 * can resume on a cold load without having started the job itself.
 */
export interface CalculationJobStatus extends CalculationStatus {
  /** Always present here — this read is BY job id. */
  jobId: string;
  /**
   * Has this job's result actually landed in the table the results endpoint
   * reads?
   *
   * `status === 'completed'` and "the citizen can see a score" are two
   * different facts, and production has produced the pair where they
   * disagree: the result is written and the assessment write-back matches
   * zero rows, leaving the assessment stuck on `calculating` forever. Gate
   * "show the result" on this, never on `status` alone.
   */
  resultReady: boolean;
}

/**
 * Cultural Risk Archetype — one of the ~10 archetypes Brain scores against
 * a talent's CheckUp inputs. Surfaced in the admin Vetting drawer's
 * Archetypes tab as a radar chart + low/moderate/high risk callouts.
 */
export interface CheckUpCRAItem {
  id: string;
  name: string;
  probability: number;
  intensity: number;
  finalScore: number;
  severity: 'low' | 'medium' | 'high';
  description: string;
  mitigation?: string;
}

/** A Skills Thermometer entry as the server sends it. */
export interface CheckUpResultSkill {
  skill: string;
  type: string;
  powerBlended: number;
  /** Graded by the server (`resolveSkillBand`) — draw it, never grade. */
  band: CheckUpSkillBand;
  flags?: string[];
}

export interface CheckUpResults {
  talentId: string;
  calculationStatus: 'completed' | 'in_progress' | 'failed';
  calculationCompletedAt?: string;
  isLatest?: boolean;
  calculatedAt: string;
  version: number;
  iqulture: {
    score: {
      /**
       * `null` = NOT SCORED, not zero.
       *
       * The engine can legitimately fail to measure a foundation — M, most
       * often, because every motivational question is optional and the API
       * accepts empty answers, so an assessment can arrive carrying no usable
       * text. It used to reach clients as `0`, which they rendered as a hard
       * zero out of a hundred and fed into every chart. `flags` says why.
       */
      m: number | null;
      e: number | null;
      s: number | null;
      h: number | null;
      finalScore: number;
      badge: string;
      badgeMeta: {
        /** The server's badge id — draw it; the sibling `badge` is a display label. */
        badgeId: IQultureBadgeId;
        label: string;
        reason: string;
      };
      /** Where the badges begin — the server's rule; draw tier lines from it, never a constant. */
      badgeLowerBounds: IQultureBadgeLowerBounds;
      /**
       * Why a foundation is unscored or weakly supported —
       * `no_motivation_data`, `low_motivation_signal`, `missing_m`, `no_pitch`.
       * Absent on results calculated before this shipped: those runs never
       * recorded a reason, which is the honest reading.
       */
      flags?: string[];
      /**
       * The score is a LOWER bound — some evidence is missing (Option B, engine
       * 3.0.0): its coverage, the range the complete evidence lands in, and the
       * points completing each foundation could add. `null` when complete (or
       * calculated before 3.0.0). Draw it; never compute from it.
       */
      provisional: IQultureCompleteness | null;
    };
    badge: string;
    diagnosis: {
      talent: {
        snapshot?: string;
        conclusion: string;
        /**
         * True when `snapshot`/`conclusion` are the transform's synthesized
         * safety-net ("Assessment complete" / "Your IQulture badge is X"),
         * not real Brain narrative. Computed server-side on the EN source so
         * consumers can hide the placeholder reliably across locales (the
         * text itself is translate-on-read'd, so string-matching it fails in
         * non-EN). See `checkup-result-transform.service.ts`.
         */
        snapshotIsPlaceholder?: boolean;
        conclusionIsFallback?: boolean;
        foundations: {
          motivation_m?: string[];
          english_native_e?: string[];
          soft_cultural_s?: string[];
          hard_other_h?: string[];
        };
        plan90Days?: string[];
        /**
         * Per-foundation strengths/improvements lists. Always present
         * when Brain produced a diagnosis — the transform spreads each
         * sub-array with `?? []` so consumers never have to handle
         * partial shapes. Used by the admin Vetting drawer's MESH
         * Analysis tab as the per-quadrant fallback when
         * `diagnosis.company.operational` doesn't carry an explicit
         * MESH-letter tag. See `checkup-result-transform.service.ts`.
         *
         * The whole `structuredFoundations` block is optional only
         * because legacy rows pre-dating it may exist; new rows always
         * carry it.
         */
        structuredFoundations?: {
          motivationM: { strengths: string[]; improvements: string[] };
          englishNativeE: { strengths: string[]; improvements: string[] };
          softCulturalS: { lowRiskCRAs: string[]; highRiskCRAs: string[] };
          hardOtherH: { strengths: string[]; improvements: string[] };
        };
      };
      /**
       * Company-side diagnosis summary. Present when Brain's pipeline
       * produced it (B2B journeys); absent for self-build journeys.
       * Drives the Vetting drawer's MESH Analysis quadrants (operational
       * insights, optionally tagged by `meshGroup`) and the Top Alerts
       * card.
       */
      company?: {
        overallSignal: 'GREEN' | 'AMBER' | 'RED';
        topAlerts: string[];
        operational: Array<{
          type: string;
          level: string;
          description: string;
          /**
           * Optional MESH grouping tag. When Brain produces it, the
           * admin drawer routes the item into the matching quadrant
           * card. Absent on legacy rows; consumers fall back to the
           * structuredFoundations.
           */
          meshGroup?: 'M' | 'E' | 'S' | 'H';
        }>;
      };
    };
  };
  /** The server's verdict reading — confidence (never defaulted) and the MESH lead. */
  insights: CheckupInsights;
  /** The citizen's profile as the calculation read it (origin persona, target market, …). */
  inputSnapshot?: {
    persona?: string;
    targetMarket?: string;
    targetRole?: string;
    workModelPreference?: string;
    cefr?: Record<string, string>;
  };
  evaluationPillars: Record<
    string,
    {
      pillarId: string;
      pillarName: string;
      score: number;
      /** The score's band, graded by the server (`pillarScoreBand`) — draw it, never grade. */
      band: CheckUpPillarBand;
      /** Brain's confidence (0–1) as reported; absent when Brain gave none. */
      confidence?: number;
      meshGroup: string;
      greenFlags: string[];
      redFlags: string[];
      evidence?: string[];
      recommendations?: string[];
    }
  >;
  skillsThermometer: {
    top10Soft: CheckUpResultSkill[];
    top10Hard: CheckUpResultSkill[];
    additional: CheckUpResultSkill[];
  };
  /**
   * Cultural Risk Archetype results. Only present when Brain's pipeline
   * produced them. The admin Vetting drawer's Archetypes tab consumes
   * `results` for the radar chart and `topRisks` for the low/moderate/high
   * risk callout columns.
   */
  cra?: {
    results: CheckUpCRAItem[];
    topRisks: CheckUpCRAItem[];
  };
  /**
   * Pitch video analysis (Brain output) plus a presigned playback URL
   * resolved from the citizen's vault upload. Null/undefined when the
   * citizen never recorded a pitch — the FE renders an empty state
   * rather than fabricating a `<video>` element with a missing src.
   */
  pitchVideoAnalysis?: {
    status: 'pending' | 'analyzing' | 'completed' | 'failed';
    /** Why a `'failed'` analysis failed; `null` on a row that predates the reason. */
    failureReason?: PitchAnalysisFailureReason | null;
    /** Present iff the delivery signals are the heuristic fallback's, not a measurement. */
    heuristicReason?: PitchHeuristicReason;
    duration?: string;
    recordedDate?: string;
    transcript?: string;
    sentiment?: string;
    confidence?: number;
    deliveryMetrics?: {
      communicationClarity?: number;
      confidenceLevel?: number;
      energyLevel?: number;
      pacing?: number;
      fillerWords?: number;
      articulation?: number;
    };
    greenFlags?: string[];
    redFlags?: string[];
    summary?: string;
    /**
     * Short-lived presigned S3 URL. Server injects this AFTER the pure
     * transform — Brain doesn't know the storage key. Null when no
     * upload has been recorded yet.
     */
    playbackUrl?: string | null;
  };
}

export interface CheckUpHistoryItem {
  id: string;
  type: string;
  status: string;
  isLatest: boolean;
  completedAt: string | null;
  /** The raw Brain blob, as stored. Draw a run from `view`, never from this. */
  results: Record<string, unknown>;
  /**
   * This run through the same transform `GET /results` answers with: the
   * score, the badge the run was awarded, graded pillars, the diagnosis. Draw a
   * past run from this — never re-derive a badge or band from `results`.
   */
  view: CheckUpResults;
  /**
   * The route (ISO codes) of the journey this run was taken for. `null` when the
   * run predates journey attribution or the journey has no origin yet.
   */
  route: { origin: string | null; destination: string | null };
}

export interface MeshBreakdown {
  M: { score: number; weight: number; contribution: number };
  E: { score: number; weight: number; contribution: number };
  S: { score: number; weight: number; contribution: number };
  H: { score: number; weight: number; contribution: number };
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

/**
 * Every CheckUp row belongs to ONE journey, and every call here names it
 * (JOURNEYS-MULTI-ROUTE-PLAN · X6): a call without one used to read or write
 * across every journey the citizen holds. Talent-level calls (the pitch video
 * and the language certificate live in the vault) take none.
 */
function forJourney(url: string, journeyId: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}journeyId=${encodeURIComponent(journeyId)}`;
}

export class CheckUpService {
  constructor(private readonly client: TonnieClient) {}

  // ─────────────────────────────────────────────────────────────────────
  // Hero CheckUp endpoints (canonical)
  // ─────────────────────────────────────────────────────────────────────

  /** Get the journey's assessment state */
  async getAssessment(talentId: string, journeyId: string): Promise<CheckUpAssessment> {
    return this.client.get<CheckUpAssessment>(
      forJourney(API_ENDPOINTS.CHECKUPS.BASE(talentId), journeyId)
    );
  }

  /** Start a new CheckUp assessment on the journey */
  async startAssessment(talentId: string, journeyId: string): Promise<CheckUpStartResult> {
    return this.client.post<CheckUpStartResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.START(talentId), journeyId),
      {}
    );
  }

  /**
   * Cancel the journey's in-progress CheckUp assessment.
   * Soft-deactivates the row so the next read sees no active
   * assessment (status flips to `not_started`). Idempotent.
   */
  async cancelAssessment(talentId: string, journeyId: string): Promise<{ cancelled: number }> {
    return this.client.post<{ cancelled: number }>(
      forJourney(API_ENDPOINTS.CHECKUPS.CANCEL(talentId), journeyId),
      {}
    );
  }

  /** Submit the journey's motivational answers */
  async submitMotivational(
    talentId: string,
    dto: { answers: Array<{ questionId: string; answer: string }> },
    journeyId: string
  ): Promise<MotivationalSubmitResult> {
    return this.client.post<MotivationalSubmitResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.MOTIVATIONAL(talentId), journeyId),
      dto
    );
  }

  /** Save the journey's motivational answers as draft (persisted to DB) */
  async saveDraftMotivational(
    talentId: string,
    dto: { answers: Array<{ questionId: string; answer: string }> },
    journeyId: string
  ): Promise<MotivationalDraftResult> {
    return this.client.put<MotivationalDraftResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.MOTIVATIONAL_DRAFT(talentId), journeyId),
      dto
    );
  }

  /** Load the journey's draft motivational answers from DB */
  async getDraftMotivational(
    talentId: string,
    journeyId: string
  ): Promise<MotivationalDraftLoadResult> {
    return this.client.get<MotivationalDraftLoadResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.MOTIVATIONAL_DRAFT(talentId), journeyId)
    );
  }

  /** Report widget completion progress (updates the journey's checkup_assessments.progress) */
  async reportWidgetProgress(
    talentId: string,
    dto: WidgetProgressInput,
    journeyId: string
  ): Promise<WidgetProgressResult> {
    return this.client.post<WidgetProgressResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.PROGRESS(talentId), journeyId),
      dto
    );
  }

  /** Submit pitch video (talent-level: the vault) */
  async submitPitchVideo(
    talentId: string,
    dto: { fileId: string; duration: number }
  ): Promise<PitchVideoResult> {
    return this.client.post<PitchVideoResult>(API_ENDPOINTS.CHECKUPS.PITCH(talentId), dto);
  }

  /** Analyze language test screenshot to extract CEFR levels (talent-level: the vault) */
  async analyzeLanguageTest(
    talentId: string,
    dto: AnalyzeLanguageTestInput
  ): Promise<LanguageTestAnalysisResult> {
    return this.client.post<LanguageTestAnalysisResult>(
      API_ENDPOINTS.CHECKUPS.LANGUAGE_TEST_ANALYZE(talentId),
      dto
    );
  }

  /**
   * Trigger the journey's IQulture MESH calculation.
   *
   * F43: `force` and `debug` were removed. The server ignores them, and they
   * were reachable by any signed-in citizen — `force` skipped the readiness
   * gate, letting an empty assessment produce a real score at the cost of a
   * full pipeline run. A forced re-run is now a company action only, resolved
   * service-to-side with an audit event.
   */
  async triggerCalculation(
    talentId: string,
    journeyId: string,
    idempotencyKey?: string
  ): Promise<CalculationTriggerResult> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['idempotency-key'] = idempotencyKey;
    }
    return this.client.post<CalculationTriggerResult>(
      forJourney(API_ENDPOINTS.CHECKUPS.CALCULATE(talentId), journeyId),
      {},
      { headers }
    );
  }

  /**
   * Get the status of ONE named calculation job, and whether its result has
   * landed. Throws a 404 `TonnieApiError` when the server has no such job for
   * this talent — a held id the server cannot see is a fact the caller must
   * handle, not one to keep polling through.
   */
  async getCalculationJobStatus(
    talentId: string,
    jobId: string,
    journeyId: string
  ): Promise<CalculationJobStatus> {
    return this.client.get<CalculationJobStatus>(
      forJourney(API_ENDPOINTS.CHECKUPS.STATUS_JOB(talentId, jobId), journeyId)
    );
  }

  /** Get the journey's full CheckUp results (null when no results yet) */
  async getResults(talentId: string, journeyId: string): Promise<CheckUpResults | null> {
    return this.client.get<CheckUpResults | null>(
      forJourney(API_ENDPOINTS.CHECKUPS.RESULTS(talentId), journeyId)
    );
  }

  /** Get the journey's MESH breakdown */
  async getMeshBreakdown(talentId: string, journeyId: string): Promise<MeshBreakdown> {
    return this.client.get<MeshBreakdown>(
      forJourney(API_ENDPOINTS.CHECKUPS.RESULTS_MESH(talentId), journeyId)
    );
  }

  /** List the journey's assessment history */
  async getHistory(talentId: string, journeyId: string): Promise<CheckUpHistoryItem[]> {
    return this.client.get<CheckUpHistoryItem[]>(
      forJourney(API_ENDPOINTS.CHECKUPS.HISTORY(talentId), journeyId)
    );
  }
}

export function createCheckUpService(client: TonnieClient): CheckUpService {
  return new CheckUpService(client);
}
